import { useState } from 'react';
import { AxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Box, CircularProgress, Typography } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import { useSnackbar } from '@/context/SnackbarContext';
import { api } from '@/utils';
import { API_ENDPOINTS } from '@/constants';

interface CallInsightsFetcherProps {
    callId: string | number;
    onSuccess?: () => void;
}

/**
 * Component that provides a button to fetch call insights from the server
 * and displays the result in the console.
 *
 * @param {CallInsightsFetcherProps} props
 * @returns {JSX.Element}
 */
const CallInsightsFetcher: React.FC<CallInsightsFetcherProps> = ({ callId, onSuccess }) => {
    const { showSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchCallInsights = async () => {
        if (!callId) {
            setError('No call ID provided');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await api.post(API_ENDPOINTS.OPENAI_CALL_INSIGHTS, { callId: Number(callId) });

            // Invalidate the call detail and the calls list so the quality score
            // badge and the calls table both reflect the newly generated formula answer.
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['api', 'calls', String(callId)] }),
                queryClient.invalidateQueries({ queryKey: ['calls'] }),
            ]);

            if (onSuccess) onSuccess();

            showSnackbar('Insights fetched successfully', 'success');
        } catch (err) {
            let errorMessage = 'Failed to fetch call insights';

            if (err instanceof AxiosError && err.response?.data?.error) {
                errorMessage = err.response.data.error;
            } else if (err instanceof Error) {
                errorMessage = err.message;
            }

            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, mt: 2 }}>
            <Button
                variant="text"
                color="primary"
                size="small"
                onClick={fetchCallInsights}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={12} color="inherit" /> : <AutoAwesome sx={{ fontSize: 12 }} />}
                sx={{ px: 1, py: 0.25, fontWeight: 'normal' }}
            >
                No insights? Try fetching them manually.
            </Button>
            {error && (
                <Typography color="error" variant="body2">
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default CallInsightsFetcher;
